import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

import IconNewBaadge from '@/assets/icons/icon_new_badge.svg';
import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { hoverOpacity } from '@/utility/theme/animation';

export const ContentBox = styled.div(() =>
  mq({
    backgroundColor: defaultTheme.background.white,
    padding: [
      defaultTheme.spacing.xxl,
      defaultTheme.spacing.xxl,
      '32px 64px',
      '32px 64px',
    ],
    marginTop: [
      defaultTheme.spacing.xs,
      defaultTheme.spacing.xs,
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
    ],
  }),
);

export const Title = styled.h2(() =>
  mq({
    fontSize: defaultTheme.font.size.l,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: [
      defaultTheme.spacing.l,
      defaultTheme.spacing.l,
      defaultTheme.spacing.xxl,
      defaultTheme.spacing.xxl,
    ],
  }),
);

export const TitleSecondary = Title.withComponent('h3');

export const TitleTertiary = styled.p(() =>
  mq({
    fontSize: defaultTheme.font.size.l,
    fontWeight: 'bold',
    lineHeight: defaultTheme.font.height.base,
    textAlign: 'center',
    marginBottom: defaultTheme.spacing.l,
  }),
);

export const TitleSmall = styled.p((props: { textAlign?: string }) =>
  mq({
    fontSize: defaultTheme.font.size.m,
    fontWeight: 'bold',
    lineHeight: defaultTheme.font.height.base,
    textAlign: props.textAlign || 'center',
    marginBottom: defaultTheme.spacing.l,
  }),
);

export const TitleBox = styled.p(() =>
  mq({
    fontSize: defaultTheme.font.size.m,
    fontWeight: 'bold',
    lineHeight: [
      defaultTheme.font.height.l,
      defaultTheme.font.height.l,
      defaultTheme.font.height.base,
      defaultTheme.font.height.base,
    ],
    textAlign: ['center', 'center', 'left', 'left'],
    marginBottom: defaultTheme.spacing.l,
  }),
);

export const Text = styled.p(() =>
  mq({
    fontSize: defaultTheme.font.size.s,
    '& > a': {
      textDecorationLine: 'underline',
      ...hoverOpacity,
    },
  }),
);

export const TextLarge = styled.p(() =>
  mq({
    fontSize: defaultTheme.font.size.m,
  }),
);

export const TextSmall = styled.p(() =>
  mq({
    fontSize: defaultTheme.font.size.xs,
    lineHeight: defaultTheme.font.height.base,
    textAlign: 'center',
    marginBottom: defaultTheme.spacing.s,
  }),
);

export const TextNote = styled.p(() =>
  mq({
    display: 'flex',
    fontSize: defaultTheme.font.size.xs,
    marginTop: defaultTheme.spacing.l,
    '&:first-of-type': {
      marginTop: 0,
    },
    '&:not(:last-of-type)': {
      marginBottom: defaultTheme.spacing.l,
    },
    '& > i': {
      fontStyle: 'normal',
    },
  }),
);

export const TextNoteList = styled.ul(() =>
  mq({
    marginTop: defaultTheme.spacing.l,
    '& > li': {
      display: 'flex',
      fontSize: defaultTheme.font.size.xs,
      lineHeight: defaultTheme.font.height.l,
      marginTop: defaultTheme.spacing.xs,
      '& > i': {
        fontStyle: 'normal',
      },
    },
  }),
);

export const TextLink = styled(Link)(() =>
  mq({
    textDecorationLine: 'underline',
    ...hoverOpacity,
  }),
);

export const ButtonContainer = styled.div(() =>
  mq({
    textAlign: 'center',
    width: '100%',
    maxWidth: '320px',
    margin: `${defaultTheme.spacing.xxl} auto 0`,
    '&:not(:last-of-type)': {
      marginBottom: defaultTheme.spacing.xxl,
    },
  }),
);

export const LinkButtonSmall = styled(Link)(() =>
  mq({
    display: 'inline-block',
    fontSize: defaultTheme.font.size.s,
    lineHeight: defaultTheme.font.height.base,
    border: `1px solid ${defaultTheme.border.color.black}`,
    borderRadius: defaultTheme.border.radius.s,
    padding: `${defaultTheme.spacing.m} ${defaultTheme.spacing.l}`,
    ...hoverOpacity,
  }),
);

export const Box = styled.div(() =>
  mq({
    display: 'grid',
    gridTemplateColumns: ['1fr', '1fr', '80px 1fr', '80px 1fr'],
    gridGap: [
      defaultTheme.spacing.l,
      defaultTheme.spacing.l,
      defaultTheme.spacing.xxl,
      defaultTheme.spacing.xxl,
    ],
  }),
);

export const BoxImage = styled.div(() =>
  mq({
    display: 'flex',
    justifyContent: 'center',
  }),
);

export const BoxImageNew = styled(BoxImage)(() =>
  mq({
    position: 'relative',
    '&::after': {
      position: 'absolute',
      left: ['50%', '50%', '-32px', '-32px'],
      transform: ['translateX(-72px)', 'translateX(-72px)', 'unset', 'unset'],
      content: '""',
      background: `url(${IconNewBaadge}) no-repeat center`,
      backgroundSize: 'cover',
      width: '48px',
      height: '48px',
    },
  }),
);

export const Center = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  }),
);

export const Flow = styled.ol(() =>
  mq({
    paddingLeft: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.xxl,
      defaultTheme.spacing.xxl,
    ],
    marginTop: defaultTheme.spacing.xxl,
    '& > li': {
      position: 'relative',
      backgroundColor: defaultTheme.background.lightGray,
      padding: `${defaultTheme.spacing.xxl} ${defaultTheme.spacing.xxl} ${defaultTheme.spacing.xxl} 48px`,
      marginTop: defaultTheme.spacing.s,
    },
  }),
);

export const FlowStepIcon = styled.div(() =>
  mq({
    position: 'absolute',
    left: [
      `-${defaultTheme.spacing.l}`,
      `-${defaultTheme.spacing.l}`,
      `-${defaultTheme.spacing.xxl}`,
      `-${defaultTheme.spacing.xxl}`,
    ],
  }),
);

export const Dl = styled.dl(() =>
  mq({
    fontSize: defaultTheme.font.size.s,
    lineHeight: defaultTheme.font.height.l,
    '& > dt': {
      fontWeight: 'bold',
      marginTop: defaultTheme.spacing.l,
    },
  }),
);
